import React, { useEffect, useRef, useState } from 'react';
import { CalendarDaysIcon } from '@heroicons/react/24/outline';
import { MiniCalendar, formatWeekdayDate, parseDateOnly, type CalendarMonth } from '@odo/shared-ui';

interface DateFieldProps {
    value: string;
    onChange: (date: string) => void;
    /** date-only ('YYYY-MM-DD') -> appointment count, for the same count
     *  dots the left-rail calendar shows. */
    counts: Record<string, number>;
    todayIso: string;
}

function monthFromIso(dateOnly: string): CalendarMonth {
    const date = parseDateOnly(dateOnly);
    return { year: date.getFullYear(), month: date.getMonth() };
}

/** Styled trigger button that opens a popover with a separate, always-
 *  interactive `MiniCalendar` instance, replacing `<input type="date">`. */
const DateField: React.FC<DateFieldProps> = ({ value, onChange, counts, todayIso }) => {
    const [open, setOpen] = useState(false);
    const [month, setMonth] = useState<CalendarMonth>(() => monthFromIso(value || todayIso));
    const containerRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (!open) return;
        const handleMouseDown = (e: MouseEvent) => {
            if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
                setOpen(false);
            }
        };
        document.addEventListener('mousedown', handleMouseDown);
        return () => document.removeEventListener('mousedown', handleMouseDown);
    }, [open]);

    const handleToggle = () => {
        setOpen((wasOpen) => {
            if (!wasOpen) setMonth(monthFromIso(value || todayIso));
            return !wasOpen;
        });
    };

    // Escape closes just the popover; stopping propagation keeps it from
    // also bubbling to the modal's own Escape stack.
    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Escape' && open) {
            e.stopPropagation();
            setOpen(false);
        }
    };

    return (
        <div ref={containerRef} onKeyDown={handleKeyDown} className="relative">
            <button
                type="button"
                onClick={handleToggle}
                aria-haspopup="dialog"
                aria-expanded={open}
                className="w-full flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-3 py-2.5 text-sm text-gray-800 hover:border-gray-400 focus:outline-none focus:border-gray-400 transition-colors"
            >
                <CalendarDaysIcon className="h-4 w-4 text-gray-400 shrink-0" />
                <span className="flex-1 text-left truncate">
                    {value ? formatWeekdayDate(value) : 'Select a date'}
                </span>
            </button>
            {open && (
                <div className="absolute z-20 mt-2 w-[300px] rounded-xl border border-gray-200 bg-white shadow-lg p-4">
                    <MiniCalendar
                        month={month}
                        onMonthChange={setMonth}
                        counts={counts}
                        selected={value}
                        todayIso={todayIso}
                        onSelect={(day) => {
                            if (day) onChange(day);
                            setOpen(false);
                        }}
                    />
                </div>
            )}
        </div>
    );
};

export default DateField;
